import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import autoTable from 'jspdf-autotable';
import jsPDF from "jspdf";
import moment from "moment";
import React, { useState, useEffect } from 'react'

import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";

import { callAddFontNirmalaBold } from '../../../styles/fonts/Nirmala-bold';
import { callAddFontNirmalaNormal } from '../../../styles/fonts/Nirmala-normal';
import { excelConfig, filterHeaderXlStyle, filterHeaderXlDataStyle } from '../../../configs/excelConfig';
import { fetchApi } from '../../../services/apiService';
import { hasAccess, NumberFormatter, jsPdfHeaderFooter, jsPdfDidParseCellCallback, jsPdfWillDrawCellCallback } from '../../../components/generics/helper';
import { Loader } from '../../../components/generics/Loader';
import { REPORT_TABLE_PAGINATION_ROWS_OPTIONS, EXPORT_PER_REQUEST_RECORD_LIMIT, REPORT_TABLE_PAGINATION_ROWS_OPTIONS_SELECTED } from "../../../constants/commonConstants";
import { StyledTableRow, StyledTableCell } from "../../../components/Table/TableStyle";
import CommonTableHeaderRow from '../../../components/Table/CommonTableHeaderRow';
import CommonTablePagination from '../../../components/Table/CommonTablePagination';
import excelExport from '../../../services/exports/export-excel';

const nowrapCellStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};
const PurchaseInvoiceReportList = (props) => {
    const { filters, loadData, setLoadData } = props
    const { enqueueSnackbar } = useSnackbar();

    const { selectedRolesRights = [], adminType = 0 } = useSelector((state) => state.login?.userData);
    const userToken = useSelector((state) => state.login.userToken);
    const userData = useSelector((state) => state.login.userData);

    const [isPdfLoading, setIsPdfLoading] = useState(false);
    const [isPdfLoaded, setIsPdfLoaded] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNo, setPageNo] = useState(0);
    const [perPageRecords, setPerPageRecords] = useState(REPORT_TABLE_PAGINATION_ROWS_OPTIONS_SELECTED);

    const getPIRSearchParams = (filters, page = 1, perPage = EXPORT_PER_REQUEST_RECORD_LIMIT) => {
        let PIRSearchParams = {}

        if (filters?.searchBillBranchIds !== '') {
            PIRSearchParams = { ...PIRSearchParams, searchPIBillBranchIds: filters?.searchBillBranchIds };
        }
        if (filters?.searchShipBranchIds !== '') {
            PIRSearchParams = { ...PIRSearchParams, searchPIShipBranchIds: filters?.searchShipBranchIds };
        }
        if (filters?.searchCategoryIds !== '') {
            PIRSearchParams = { ...PIRSearchParams, searchPIItemCategoryIds: filters?.searchCategoryIds };
        }
        if (filters?.searchSubCategoryIds !== '') {
            PIRSearchParams = { ...PIRSearchParams, searchPIItemSubCategoryIds: filters?.searchSubCategoryIds };
        }
        if (filters?.searchBrandIds !== '') {
            PIRSearchParams = { ...PIRSearchParams, searchPIItemBrandIds: filters?.searchBrandIds };
        }
        if (filters?.searchItemIds !== '') {
            PIRSearchParams = { ...PIRSearchParams, searchPIItemIds: filters?.searchItemIds };
        }
        if (filters?.searchVendorIds !== '') {
            PIRSearchParams = { ...PIRSearchParams, searchVendorIds: filters?.searchVendorIds };
        }

        PIRSearchParams.pageNo = page;
        PIRSearchParams.perPageRecords = perPage;
        PIRSearchParams.formattedDate = "Y";
        PIRSearchParams.dateType = filters.dateType;
        if (filters.dateType === 'I') {
            PIRSearchParams.searchPIInvFromDate = moment(filters?.fromDate).format('YYYY-MM-DD');
            PIRSearchParams.searchPIInvToDate = moment(filters?.toDate).format('YYYY-MM-DD');
        } else {
            PIRSearchParams.searchPIRecFromDate = moment(filters?.fromDate).format('YYYY-MM-DD');
            PIRSearchParams.searchPIRecToDate = moment(filters?.toDate).format('YYYY-MM-DD');
        }

        return PIRSearchParams;
    };
    const generatePurchaseInvoiceData = async (page = 0, rowsPerPage = perPageRecords) => {
        setIsLoading(true);
        try {
            const diffDays = moment(filters.toDate).diff(moment(filters.fromDate), 'days');
            if (diffDays > 90) {
                enqueueSnackbar('Date range cannot exceed 90 days', { variant: 'error' });
                setIsLoading(false);
                return;
            }
            const PIRSearchParams = getPIRSearchParams(filters, page + 1, rowsPerPage);

            const response = await fetchApi('reports/getPurchaseInvoiceReportData', PIRSearchParams, userToken);
            setIsLoading(false);

            if (response.status === 'Ok' && response.data.responseStatus === 'Ok') {
                if (response.data.piReportData.length > 0) {
                    enqueueSnackbar('Purchase Invoice Report Data Loaded Successfully', { variant: 'success', key: Date.now() });
                    setTableData(response?.data?.piReportData);
                    setTotalRecords(response.data.totalRecords);
                } else {
                    enqueueSnackbar('Ooops! No Record Found', { variant: 'error' });
                    setTableData([]);
                }
            } else {
                enqueueSnackbar(response.errorMessage, { variant: 'error' });
                setTableData([]);
                setTotalRecords(0);
            }
        } catch (err) {
            let message = 'Purchase Invoice Report List Loading Failed';
            if (!err.message) {
                err.message = message
            }
            enqueueSnackbar(err.message, { variant: 'error' });
            setTableData([])
            setTotalRecords(0);
        } finally {
            setLoadData(false);
        }
    }

    const exportAllPurchaseInvoiceReportData = async () => {
        const allPIRData = [];
        try {
            const PIRSearchParams = getPIRSearchParams(filters);
            const totalPages = Math.ceil(totalRecords / PIRSearchParams.perPageRecords);
            for (let page = 1; page <= totalPages; page++) {
                const pageParams = { ...PIRSearchParams, pageNo: page };
                const response = await fetchApi('reports/getPurchaseInvoiceReportData', pageParams, userToken);
                if (response.status === 'Ok' && response.data.responseStatus === 'Ok') {
                    allPIRData.push(...response.data.piReportData);
                }
            }
            return allPIRData;
        } catch (err) {
            let message = 'Purchase Invoice Report List Export Failed';
            if (!err.message) {
                err.message = message
            }
            enqueueSnackbar(err.message, { variant: 'error' });
            return [];
        }
    }

    const handleChangePage = (event, newPage) => {
        setPageNo(newPage);
        generatePurchaseInvoiceData(newPage, perPageRecords);
    };
    const handleChangeRowsPerPage = (event) => {
        setPerPageRecords(parseInt(event.target.value, 10));
        setPageNo(0);
        generatePurchaseInvoiceData(0, parseInt(event.target.value, 10));
    };

    const columnDefs = [
        { headerName: 'Po Date', pdfHead: 'Po Date', xlCol: { dataType: 'string', dataFormatter: null, width: 15 }, field: 'poDate', headerSx: { width: '130px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Po Number', pdfHead: 'Po No. ', field: 'poNo', xlCol: { dataType: 'string', dataFormatter: null, width: 16 }, headerSx: { width: "150px", textAlign: "left" }, allowSorting: false },
        { headerName: 'Pi Date', pdfHead: 'Pi Date', xlCol: { dataType: 'string', dataFormatter: null, width: 15 }, field: 'invDate', headerSx: { width: '130px', textAlign: "left" }, allowSorting: false },
        { headerName: 'GRN', pdfHead: 'GRN', xlCol: { dataType: 'string', dataFormatter: null, width: 18 }, field: 'grnNo', headerSx: { width: "100px", textAlign: 'left' }, allowSorting: false },
        { headerName: 'Purchase Type', pdfHead: 'Purchase Type', field: 'purchaseTypeTitle', xlCol: { dataType: 'string', dataFormatter: null, width: 18 }, headerSx: { width: "150px", textAlign: 'left', whiteSpace: 'nowrap' }, allowSorting: false },
        { headerName: 'Pi No.', pdfHead: 'Pi No. ', field: 'invNo', xlCol: { dataType: 'string', dataFormatter: null, width: 18 }, headerSx: { width: "150px", textAlign: "left" }, allowSorting: false },
        { headerName: 'Bill To Branch', pdfHead: 'Bill Branch', xlCol: { dataType: 'string', dataFormatter: null, width: 40 }, field: 'billToBranchName', headerSx: { width: '150px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Ship To Branch', pdfHead: 'Ship Branch', xlCol: { dataType: 'string', dataFormatter: null, width: 40 }, field: 'shipToBranchName', headerSx: { width: '150px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Vendor', pdfHead: 'Vendor', xlCol: { dataType: 'string', dataFormatter: null, width: 30 }, field: 'vendorName', headerSx: { width: '120px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Category', pdfHead: 'Category', field: 'categoryTitle', xlCol: { dataType: 'string', dataFormatter: null, width: 20 }, headerSx: { width: '120px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Subcategory', pdfHead: 'Sub cat', field: 'subCategoryTitle', xlCol: { dataType: 'string', dataFormatter: null, width: 20 }, headerSx: { width: '120px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Brand', pdfHead: 'Brand', field: 'brandName', xlCol: { dataType: 'string', dataFormatter: null, width: 15 }, headerSx: { width: '100px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Item Name', pdfHead: 'Item', xlCol: { dataType: 'string', dataFormatter: null, width: 30 }, field: 'itemName', headerSx: { width: '150px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Part No.', pdfHead: 'Part No. ', field: 'partNo', xlCol: { dataType: 'string', dataFormatter: null, width: 15 }, headerSx: { width: '100px', textAlign: "left" }, allowSorting: false },
        { headerName: 'UOM', pdfHead: 'UOM', field: 'unitTitle', xlCol: { dataType: 'string', dataFormatter: null, width: 15 }, headerSx: { width: '100px', textAlign: "left" }, allowSorting: false },
        { headerName: 'QTY', pdfHead: 'QTY', field: 'qty', xlCol: { dataType: 'quantity', dataFormatter: null, width: 15 }, headerSx: { width: '100px', textAlign: "right" }, allowSorting: false },
        { headerName: 'Unit Rate', pdfHead: 'Unit Rate', field: 'unitPrice', xlCol: { dataType: 'currency', dataFormatter: null, width: 15 }, headerSx: { width: '100px', textAlign: "right" }, allowSorting: false },
        { headerName: 'Base Value', pdfHead: 'Base Val', field: 'baseValue', xlCol: { dataType: 'currency', dataFormatter: null, width: 15 }, headerSx: { width: '100px', textAlign: "right" }, allowSorting: false },
        { headerName: 'Dis %', pdfHead: 'Dis %', field: 'disPer', xlCol: { dataType: 'number', dataFormatter: null, width: 15 }, headerSx: { width: '100px', textAlign: "right" }, allowSorting: false },
        { headerName: 'Dis Amt', pdfHead: 'Dis Amt', field: 'disAmt', xlCol: { dataType: 'currency', dataFormatter: null, width: 16 }, headerSx: { width: '120px', textAlign: "right" }, allowSorting: false },
        { headerName: 'Taxable Amt', pdfHead: 'Tax Amt', field: 'taxableAmount', xlCol: { dataType: 'currency', dataFormatter: null, width: 16 }, headerSx: { width: '120px', textAlign: "right" }, allowSorting: false },
        { headerName: 'GST %', pdfHead: 'GST %', field: 'gstPer', xlCol: { dataType: 'number', dataFormatter: null, width: 15 }, headerSx: { width: '100px', textAlign: "right" }, allowSorting: false },
        { headerName: 'GST Amt', pdfHead: 'GST Amt', field: 'gstAmount', xlCol: { dataType: 'currency', dataFormatter: null, width: 15 }, headerSx: { width: '103px', textAlign: "right" }, allowSorting: false },
        { headerName: 'Final Rate', pdfHead: 'Final Rate', field: 'grossAmount', xlCol: { dataType: 'currency', dataFormatter: null, width: 20 }, headerSx: { width: '123px', textAlign: "right" }, allowSorting: false },
        { headerName: 'Landing Price', pdfHead: 'Landing Price', field: 'landingPrice', xlCol: { dataType: 'currency', dataFormatter: null, width: 20 }, headerSx: { width: '123px', textAlign: "right" }, allowSorting: false },
        { headerName: 'Vehicle No.', pdfHead: 'Vehicle No.', field: 'vehicleNo', xlCol: { dataType: 'string', dataFormatter: null, width: 20 }, headerSx: { width: '150px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Vehicle Code', pdfHead: 'Vehicle Code', field: 'vehicleCode', xlCol: { dataType: 'string', dataFormatter: null, width: 20 }, headerSx: { width: '120px', textAlign: "left" }, allowSorting: false },
    ];

    const getFilterSectionData = () => {
        return {
            filterHeaderXlStyle,
            filterHeaderXlDataStyle,
            filterData: [
                {
                    columns: [
                        { header: 'Filter Date Type', dataKey: 'filterDateType', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 2 } },
                        { header: filters.dateType === 'I' ? 'Invoice From Date' : 'Receiving From Date', dataKey: 'fromDate', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 2 } },
                        { header: filters.dateType === 'I' ? 'Invoice To Date' : 'Receiving To Date', dataKey: 'toDate', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 1 } },
                        { header: 'BillBranch', dataKey: 'billToBranchName', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 1 } },
                        { header: 'ShipBranch', dataKey: 'shipToBranchName', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 2 } },
                    ],
                    pdfColumnStyles: {
                        0: { cellWidth: 50, halign: 'left', fontSize: 10 },
                        1: { cellWidth: 50, halign: 'left', fontSize: 10 },
                        2: { cellWidth: 50, halign: 'left', fontSize: 10 },
                        3: { cellWidth: 60, halign: 'left', fontSize: 10 },
                        4: { cellWidth: 60, halign: 'left', fontSize: 10 },
                    },
                    data: [
                        {
                            filterDateType: filters.dateType === 'I' ? 'Invoice Date' : 'Receiving Date',
                            fromDate: moment(filters.fromDate).format('DD-MMM-YY'),
                            toDate: moment(filters.toDate).format('DD-MMM-YY'),
                            billToBranchName: filters.searchBillBranchIds === "" ? 'All Branches' : filters.searchBillBranchIds.split(',').length > 1 ? `Multiple [ ${filters.searchBillBranchIds.split(',').length} ]` : filters?.searchBillBranchNames[0],
                            shipToBranchName: filters.searchShipBranchIds === "" ? 'All Branches' : filters.searchShipBranchIds.split(',').length > 1 ? `Multiple [ ${filters.searchShipBranchIds.split(',').length} ]` : filters?.searchShipBranchNames[0],
                        }
                    ]
                },
                {
                    columns: [
                        { header: 'Items', dataKey: 'itemName', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 2 } },
                        { header: 'Category', dataKey: 'categoryTitle', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 2 } },
                        { header: 'Subcategory', dataKey: 'subCategoryTitle', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 1 } },
                        { header: 'Brand', dataKey: 'brandName', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 1 } },
                        { header: 'Vendor', dataKey: 'vendorName', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 2 } },
                    ],
                    pdfColumnStyles: {
                        0: { cellWidth: 50, halign: 'left', fontSize: 10 },
                        1: { cellWidth: 50, halign: 'left', fontSize: 10 },
                        2: { cellWidth: 50, halign: 'left', fontSize: 10 },
                        3: { cellWidth: 60, halign: 'left', fontSize: 10 },
                        4: { cellWidth: 60, halign: 'left', fontSize: 10 },
                    },
                    data: [
                        {
                            itemName: filters.searchItemIds === "" ? 'All Items' : filters.searchItemIds.split(',').length > 1 ? `Multiple [ ${filters.searchItemIds.split(',').length} ]` : filters?.searchItemNames[0],
                            categoryTitle: filters.searchCategoryIds === "" ? 'All Categories' : filters.searchCategoryIds.split(',').length > 1 ? `Multiple [ ${filters.searchCategoryIds.split(',').length} ]` : (filters?.searchCategoryNames || [])[0],
                            subCategoryTitle: filters.searchSubCategoryIds === "" ? 'All Sub Categories' : filters.searchSubCategoryIds.split(',').length > 1 ? `Multiple [ ${filters.searchSubCategoryIds.split(',').length} ]` : (filters?.searchSubCategoryNames || [])[0],
                            brandName: filters.searchBrandIds === "" ? 'All Brands' : filters.searchBrandIds.split(',').length > 1 ? `Multiple [ ${filters.searchBrandIds.split(',').length} ]` : (filters?.searchBrandNames || [])[0],
                            vendorName: filters.searchVendorIds === "" ? 'All Vendors' : filters.searchVendorIds.split(',').length > 1 ? `Multiple [ ${filters.searchVendorIds.split(',').length} ]` : filters?.searchVendorNames[0],
                        }
                    ]
                },
            ]
        }
    }

    const printReport = async () => {
        try {
            const filterSectionData = getFilterSectionData();
            setIsPdfLoading(true);
            setIsPdfLoaded(false);
            const pdfColumns = columnDefs.map((col) => {
                return { header: col.pdfHead, dataKey: col.field, xlCol: col.xlCol }
            });

            const allData = await exportAllPurchaseInvoiceReportData();
            const fullData = allData.filter(row => row.isDataRow);

            jsPDF.API.events.push(["addFonts", callAddFontNirmalaBold]);
            jsPDF.API.events.push(["addFonts", callAddFontNirmalaNormal]);
            const doc = new jsPDF({
                orientation: 'l',
                format: 'legal',
                compress: true,
            });
            var totalPagesExp = '{total_pages_count_string}';
            doc.autoTableSetDefaults({
                headStyles: { fontSize: 6, halign: 'left', fillColor: [30, 89, 167] },
            });
            doc.setProperties({
                title: `purchaseInvoiceReport-${moment().format('DD-MMM-YY-HH-mm')}.pdf`,
                subject: "Purchase Invoice Report Report",
                author: "Maven Tech Labs Pvt. Ltd.",
                keywords: "maven tech, Maintenance system, purchase invoice",
                creator: "MTLPL",
            });
            autoTable(doc, {
                margin: { top: 16, left: 5, right: 5, bottom: 5 },
                columns: filterSectionData.filterData[0].columns,
                body: filterSectionData.filterData[0].data,
                columnStyles: filterSectionData.filterData[0].pdfColumnStyles,
                theme: 'grid',
                tableWidth: 'wrap',
            });
            autoTable(doc, {
                startY: doc.lastAutoTable.finalY,
                margin: { top: 16, left: 5, right: 5, bottom: 5 },
                columns: filterSectionData.filterData[1].columns,
                body: filterSectionData.filterData[1].data,
                columnStyles: filterSectionData.filterData[1].pdfColumnStyles,
                theme: 'grid',
                tableWidth: 'wrap',
            });
            doc.line(0, doc.lastAutoTable.finalY + 2, doc.getPageWidth(), doc.lastAutoTable.finalY + 2);
            autoTable(doc, {
                startY: doc.lastAutoTable.finalY + 4,
                tableWidth: 'auto',
                margin: { top: 2, left: 5, right: 5, bottom: 5 },
                alternateRowStyles: {
                    fillColor: [255, 255, 255]
                },
                columns: pdfColumns,
                body: fullData,
                columnStyles: {
                    0: { cellWidth: 16 },
                    1: { minCellWidth: 12 },
                    2: { cellWidth: 16 },
                    3: { minCellWidth: 12 },
                    4: { minCellWidth: 16 },
                    5: { minCellWidth: 8 },
                    6: { cellWidth: 21 },
                    7: { cellWidth: 21 },
                    8: { cellWidth: 16 },
                    9: { minCellWidth: 15 },
                    10: { minCellWidth: 10 },
                    11: { minCellWidth: 11 },
                    12: { minCellWidth: 15 },
                    13: { cellWidth: 11, halign: 'right' },
                    14: { minCellWidth: 9 },
                    15: { minCellWidth: 10, halign: 'right' },
                    16: { minCellWidth: 12, halign: 'right' },
                    17: { minCellWidth: 15, halign: 'right' },
                    18: { minCellWidth: 8, halign: 'right' },
                    19: { minCellWidth: 8, halign: 'right' },
                    20: { minCellWidth: 15, halign: 'right' },
                    21: { minCellWidth: 8, halign: 'right' },
                    22: { minCellWidth: 10, halign: 'right' },
                    23: { minCellWidth: 15, halign: 'right' },
                    24: { minCellWidth: 12, halign: 'right' },
                    25: { minCellWidth: 12 },
                    26: { minCellWidth: 11 },
                },
                theme: 'grid',
                bodyStyles: {
                    fontSize: 6,
                    halign: 'left',
                },
                didParseCell: (data) => {
                    jsPdfDidParseCellCallback(data, pdfColumns,);
                },
                willDrawCell: (data) => {
                    jsPdfWillDrawCellCallback(data);
                },
                didDrawPage: function (data) {
                    data.settings.margin.top = 16;
                    const reportTitle = `Maintenance System | Purchase Invoice Report`;
                    jsPdfHeaderFooter(data, reportTitle, userData, totalPagesExp);
                },
            },
            );
            if (typeof doc.putTotalPages === 'function') {
                doc.putTotalPages(totalPagesExp)
            }
            setIsPdfLoaded(true);
            setTimeout(() => {
                // document.getElementById('output').data = doc.output('datauristring', `purchaseInvoiceSummary_${moment(new Date()).format("DD_MMM_YYYY_ddd_HH_mm")}.pdf`);
                const blob = doc.output('blob');
                const url = URL.createObjectURL(blob);
                document.getElementById('output').data = url;
                setIsPdfLoading(false);
            }, 1000);
        } catch (err) {
            console.log(err);
            setIsPdfLoading(false);
            setIsPdfLoaded(false);
        }
    }

    const reportExcelExport = async () => {
        try {
            const filterSectionData = getFilterSectionData();
            setIsPdfLoading(true);
            const xlColumns = columnDefs.map((col) => {
                return { colHeader: col.headerName, field: col.field, xlCol: col.xlCol }
            });
            const reportTitle = `Maintenance System | Purchase Invoice Report`
            const xlConfig = {
                ...excelConfig,
                columns: xlColumns,
                fileName: `Purchase_Invoice_Report_${moment(new Date()).format("DD_MMM_YYYY_ddd_HH_mm")}.xlsx`,
                showFilterSection: true,
                filterSectionData,
                workSheetTitle: `Purchase_Invoice_Report`,
                headerFooter: {
                    firstHeader: `&L&14&K104FA2 ${reportTitle} &R&B&12 Company Name: ${userData.companyName}`,
                    firstFooter: `&L&12 Report By : ${userData.userFullName}  [ ${userData.branchName} ] &C&12 Report Time :  ${moment(new Date()).format("DD MMM YYYY, ddd, HH:mm")} &R&12 Page: &P of &N`
                },
                sheetHeaderData: [{
                    "caption": "Report Name :",
                    "value": `Purchase Invoice Report`,
                    "colSpan": 5
                }, {
                    "caption": "Company Name :",
                    "value": userData.companyName,
                    "colSpan": 6
                }],
            };
            const allData = await exportAllPurchaseInvoiceReportData();
            const fullData = allData.filter(row => row.isDataRow);
            await excelExport.processExcel(xlConfig, fullData);
            setIsPdfLoading(false);
        } catch (err) {
            console.log(err);
            setIsPdfLoading(false);
        }
    }
    const backToScreenView = () => {
        setIsPdfLoaded(false);
    }

    useEffect(() => {
        if (loadData) {
            generatePurchaseInvoiceData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadData]);

    return (
        <>
            <React.Fragment key="pir">
                {isLoading && <div style={{ width: "100%", textAlign: 'center', marginTop: '10px' }}><Loader /></div>}
                {tableData.length > 0 &&
                    <React.Fragment key="pir_dataBlock">
                        <div className='report--header--container'>
                            <div key="clss_report_name" className='report--for--heading'>Purchase Invoice Report</div>
                            <div key="pir_report_action_container" className="report-export-button--container">
                                {!isPdfLoaded && <React.Fragment key="pir_Actions">
                                    {hasAccess(selectedRolesRights, '4102', adminType) && <div key="pir_xls_btn" className="dashed--btn" onClick={() => reportExcelExport()}> EXPORT AS EXCEL </div>}
                                    {hasAccess(selectedRolesRights, '4103', adminType) && <div key="pir_pdf_btn" className="dashed--btn" onClick={() => printReport()}> EXPORT AS PDF </div>}
                                    {hasAccess(selectedRolesRights, '4101', adminType) && <div key="pir_prn_btn" className="dashed--btn" onClick={() => printReport()}> PRINT </div>}
                                </React.Fragment>}
                                {isPdfLoaded && <React.Fragment key="pir_backBtn"><div key="pir_back_btn" className="dashed--btn" onClick={() => backToScreenView()}> Back </div> </React.Fragment>}
                                {isPdfLoading && <div key="pir_loading_container" style={{ width: "100px", textAlign: 'center', marginTop: '10px' }}><Loader /></div>}
                            </div>
                        </div>
                        {!isPdfLoaded && <React.Fragment key="pir_dataTable">
                            <div className="table--container">
                                <TableContainer component={Paper} className="jc--scroll" style={{ maxHeight: "85vh" }}>
                                    <Table sx={{ minWidth: 1300 }} stickyHeader aria-label="sticky table">
                                        <CommonTableHeaderRow columnDefs={columnDefs} />
                                        <TableBody>
                                            {tableData && tableData.length ? tableData.map((row, index) => (
                                                row.isDataRow ?
                                                    <StyledTableRow key={`${row.rowType}-D-${index}`} className="grid">
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.poDate}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.poNo}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.invDate}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.grnNo}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.purchaseTypeTitle}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.invNo}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.billToBranchName}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.shipToBranchName}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.vendorName}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.categoryTitle}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.subCategoryTitle}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.brandName}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.itemName}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.partNo}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.unitTitle}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle, textAlign: "right" }}>{NumberFormatter(row.qty, 3)}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle, textAlign: "right" }}>{NumberFormatter(row.unitPrice, 2)}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle, textAlign: "right" }}>{NumberFormatter(row.baseValue, 2)}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle, textAlign: "right" }}>{NumberFormatter(row.disPer, 2)}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle, textAlign: "right" }}>{NumberFormatter(row.disAmt, 2)}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle, textAlign: "right" }}>{NumberFormatter(row.taxableAmount, 2)}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle, textAlign: "right" }}>{NumberFormatter(row.gstPer, 2)}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle, textAlign: "right" }}>{NumberFormatter(row.gstAmount, 2)}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle, textAlign: "right" }}>{NumberFormatter(row.grossAmount, 2)}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle, textAlign: "right" }}>{NumberFormatter(row.landingPrice, 2)}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.vehicleNo}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.vehicleCode}</StyledTableCell>
                                                    </StyledTableRow>
                                                    : null
                                                // row.isTotalRow &&
                                                // <StyledTableRow key={`${row.rowType}-T-${index}`} style={{ background: "#e4ebf5" }} className="grid">
                                                //     <StyledTableCell colSpan={row.colSpan} style={{ width: "50px", textAlign: "left", fontWeight: "700" }}>{row.totalCaption}</StyledTableCell>
                                                //     <StyledTableCell style={{ width: "100px", textAlign: "right" }}>{row.qty > 0 ? NumberFormatter(row.qty, 3) : ' '}</StyledTableCell>
                                                //     <StyledTableCell style={{ width: "120px", textAlign: "right" }}>{row.unitPrice > 0 ? NumberFormatter(row.unitPrice, 2) : ' '}</StyledTableCell>
                                                //     <StyledTableCell style={{ width: "100px", textAlign: "right" }}>{row.baseValue > 0 ? NumberFormatter(row.baseValue, 2) : ' '}</StyledTableCell>
                                                //     <StyledTableCell style={{ width: "120px", textAlign: "right" }}>{row.disPer > 0 ? NumberFormatter(row.disPer, 2) : ' '}</StyledTableCell>
                                                //     <StyledTableCell style={{ width: "120px", textAlign: "right" }}>{row.disAmt > 0 ? NumberFormatter(row.disAmt, 2) : ' '}</StyledTableCell>
                                                //     <StyledTableCell style={{ width: "120px", textAlign: "right" }}>{row.taxableAmount > 0 ? NumberFormatter(row.taxableAmount, 2) : ' '}</StyledTableCell>
                                                //     <StyledTableCell style={{ width: "120px", textAlign: "right" }}>{row.gstPer > 0 ? NumberFormatter(row.gstPer, 2) : ' '}</StyledTableCell>
                                                //     <StyledTableCell style={{ width: "120px", textAlign: "right" }}>{row.gstAmount > 0 ? NumberFormatter(row.gstAmount, 2) : ' '}</StyledTableCell>
                                                //     <StyledTableCell style={{ width: "120px", textAlign: "right" }}>{row.grossAmount > 0 ? NumberFormatter(row.grossAmount, 2) : ' '}</StyledTableCell>
                                                // </StyledTableRow>
                                            )) :
                                                <StyledTableRow key={`$por_no_records_row`}>
                                                    <StyledTableCell style={{ width: "100%" }} colSpan={columnDefs.length} align="center">
                                                        <div key="por_no_records" className='no--records'> No Records </div>
                                                    </StyledTableCell>
                                                </StyledTableRow>}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </div>
                            <div className="report-pagination--container">
                                <CommonTablePagination
                                    rowsPerPageOption={REPORT_TABLE_PAGINATION_ROWS_OPTIONS}
                                    count={totalRecords}
                                    rowsPerPage={perPageRecords}
                                    className=""
                                    page={pageNo}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </div>
                        </React.Fragment>}
                        {isPdfLoaded && <object id="output" type="application/pdf" width="100%" height="600" title="MTLPL - Maintenance System - Purchase Invoice Report" aria-label="MTLPL - Maintenance System - Purchase Invoice Report"></object>}
                    </React.Fragment>
                }
            </React.Fragment>
        </>
    );
}
export default PurchaseInvoiceReportList;